import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Última actualización: Abril 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; estos datos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la Información
Tu información se utiliza exclusivamente para:
• Procesar y entregar tu compra.
• Enviarte el acceso al producto digital adquirido.
• Comunicarnos contigo en caso de ser necesario respecto a tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. No Compartimos tu Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos asisten en la operación de nuestro sitio y el procesamiento de pagos, quienes están obligados a mantener la confidencialidad.

5. Cookies
Utilizamos cookies esenciales para el funcionamiento del sitio. No utilizamos cookies de rastreo publicitario.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento.
`.trim();
const termsContent = `
Última actualización: Abril 2026

1. Aceptación de los Términos
Al realizar una compra en este sitio, aceptas estos términos y condiciones en su totalidad.

2. Producto Digital
"Maquinaria de Enfoque" es un producto digital (PDF) que se entrega de forma instantánea tras la confirmación del pago. No es un servicio de consultoría, coaching ni asesoría profesional.

3. Acceso y Entrega
Tras completar el pago, recibirás acceso inmediato al material digital. El acceso es de por vida y para uso personal únicamente.

4. Política de Reembolso
Debido a la naturaleza digital e instantánea del producto, no se ofrecen reembolsos una vez que el acceso ha sido entregado.

5. Propiedad Intelectual
Todo el contenido del producto está protegido por derechos de autor. Queda prohibida su reproducción, distribución o reventa sin autorización expresa.

6. Limitación de Responsabilidad
Los resultados pueden variar según cada individuo. No garantizamos resultados específicos. El contenido es educativo e informativo y no sustituye asesoría profesional.

7. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación.
`.trim();
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-35">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25
    }} className="bg-tactical-gray border border-tactical-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-36">
        <div className="flex items-center justify-between p-6 border-b border-tactical-border" data-id="element-37">
          <h2 className="text-xl font-oswald text-white uppercase tracking-wide" data-id="element-38">
            {title}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors p-1" aria-label="Cerrar" data-id="element-39">
            <XIcon className="w-5 h-5" data-id="element-40" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto text-zinc-400 text-sm leading-relaxed whitespace-pre-line" data-id="element-41">
          {content}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-tactical-gray border-t border-tactical-border py-8 px-4" data-id="element-42">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4" data-id="element-43">
          <div className="flex items-center gap-6" data-id="element-44">
            <button onClick={() => setActiveModal('privacy')} className="text-zinc-500 hover:text-accent text-sm transition-colors" data-id="element-45">
              Política de Privacidad
            </button>
            <span className="text-zinc-700" data-id="element-46">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-zinc-500 hover:text-accent text-sm transition-colors" data-id="element-47">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-zinc-600 text-sm" data-id="element-48">
            © {new Date().getFullYear()} Maquinaria de Enfoque. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-49">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-50" />}
      </AnimatePresence>
    </>;
}